import { IoEventDispatch } from "../entity/IoEvents";
import { V, V2d } from "../Vector";
import { MouseButtons } from "./MouseButtons";

/**
 * Manages mouse input state and events.
 * Tracks button states, cursor position, and dispatches events to handlers.
 */
export class MouseManager {
  private buttons = [false, false, false, false, false, false];
  private _position: V2d = V(0, 0);
  private boundOnMouseMove: (e: MouseEvent) => void;
  private boundOnMouseUp: (e: MouseEvent) => void;
  private boundOnBlur: () => void;

  constructor(
    private view: HTMLElement,
    private dispatch: IoEventDispatch,
    private onInputActivity: () => void,
  ) {
    this.view.onclick = (e) => this.onClick(e);
    this.view.onmousedown = (e) => this.onMouseDown(e);
    this.view.oncontextmenu = (e) => {
      e.preventDefault();
      this.onClick(e);
      return false;
    };

    // The pointer is tracked over the whole window, not just the canvas:
    // HUD chrome floats over the world and takes the pointer wherever it
    // opts in, and bound on the canvas alone the position would freeze
    // under every card — a carried bench tool stalling at the tool rail's
    // edge. Presses stay the canvas's own; a click on a card is the
    // card's business.
    this.boundOnMouseMove = (e) => this.onMouseMove(e);
    window.addEventListener("mousemove", this.boundOnMouseMove);
    // A press that starts on the canvas can end anywhere — over a HUD
    // card, off the edge of the window — and the button has to come up
    // wherever it lands. Bound on the canvas alone it never would, and a
    // bench stroke begun with the mouse would go on cutting forever.
    this.boundOnMouseUp = (e) => this.onMouseUp(e);
    this.boundOnBlur = () => this.releaseAllButtons();
    window.addEventListener("mouseup", this.boundOnMouseUp);
    // Released outside the window entirely, the mouseup is delivered to
    // whatever took focus instead; losing focus is the only word we get.
    window.addEventListener("blur", this.boundOnBlur);
  }

  /** Current mouse position in screen coordinates. */
  get position(): V2d {
    return this._position;
  }

  /** True if the left mouse button is down. */
  get lmb(): boolean {
    return this.buttons[MouseButtons.LEFT];
  }

  /** True if the middle mouse button is down. */
  get mmb(): boolean {
    return this.buttons[MouseButtons.MIDDLE];
  }

  /** True if the right mouse button is down. */
  get rmb(): boolean {
    return this.buttons[MouseButtons.RIGHT];
  }

  /** Returns the state of a specific mouse button. */
  isButtonDown(button: number): boolean {
    return this.buttons[button];
  }

  private updatePosition(event: MouseEvent): void {
    // Canvas-relative, Y down from its top — the same screen space the
    // renderer draws in and the camera's matrix maps to, so the pointer
    // is reported as it arrives. Measured from the canvas's own box
    // rather than offsetX/offsetY, which are relative to whichever
    // element the event landed on. (Upstream flipped Y here for a Y-up
    // world; this game's cells run downward, so a flip would mirror
    // every pick about the middle of the screen.)
    const rect = this.view.getBoundingClientRect();
    this._position = V(event.clientX - rect.left, event.clientY - rect.top);
  }

  private onMouseMove(event: MouseEvent): void {
    this.onInputActivity();
    this.updatePosition(event);
  }

  private onClick(event: MouseEvent): void {
    this.onInputActivity();
    this.updatePosition(event);
    switch (event.button) {
      case MouseButtons.LEFT:
        this.dispatch("click", undefined as void);
        break;
      case MouseButtons.MIDDLE:
        this.dispatch("middleClick", undefined as void);
        break;
      case MouseButtons.RIGHT:
        this.dispatch("rightClick", undefined as void);
        break;
    }
  }

  private onMouseDown(event: MouseEvent): void {
    this.onInputActivity();
    this.updatePosition(event);
    this.buttons[event.button] = true;
    switch (event.button) {
      case MouseButtons.LEFT:
        this.dispatch("mouseDown", undefined as void);
        break;
      case MouseButtons.MIDDLE:
        this.dispatch("middleDown", undefined as void);
        break;
      case MouseButtons.RIGHT:
        this.dispatch("rightDown", undefined as void);
        break;
    }
  }

  private onMouseUp(event: MouseEvent): void {
    this.onInputActivity();
    this.updatePosition(event);
    this.releaseButton(event.button);
  }

  private releaseAllButtons(): void {
    for (let button = 0; button < this.buttons.length; button++) {
      this.releaseButton(button);
    }
  }

  /**
   * Lift one button, if it was down. A release the world never saw press
   * — a click that began on a HUD control — stays the chrome's business.
   */
  private releaseButton(button: number): void {
    if (!this.buttons[button]) return;
    this.buttons[button] = false;
    switch (button) {
      case MouseButtons.LEFT:
        this.dispatch("mouseUp", undefined as void);
        break;
      case MouseButtons.MIDDLE:
        this.dispatch("middleUp", undefined as void);
        break;
      case MouseButtons.RIGHT:
        this.dispatch("rightUp", undefined as void);
        break;
    }
  }

  destroy(): void {
    this.view.onclick = null;
    this.view.onmousedown = null;
    this.view.oncontextmenu = null;
    window.removeEventListener("mousemove", this.boundOnMouseMove);
    window.removeEventListener("mouseup", this.boundOnMouseUp);
    window.removeEventListener("blur", this.boundOnBlur);
  }
}
